import { useState, useEffect, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/ui/dialog/Dialog';
import { Button } from '@/shared/ui/button';
import { SelectorPickerContent } from '../picker/SelectorPickerContent';
import { SelectorPickerToolbar } from '../picker/SelectorPickerToolbar';
import { SelectorPickModalFooter } from './SelectorPickModalFooter';
import type { ColumnDef } from '../types';
import type { UsePagedAsyncSuggestionsResult } from '../search/usePagedAsyncSuggestions';

export interface AsyncSelectorPickModalProps<TItem> {
  open: boolean;
  onClose: () => void;
  initialSelected: readonly TItem[];
  mode: 'single' | 'multi';
  columns: readonly ColumnDef<TItem>[];
  suggestions: UsePagedAsyncSuggestionsResult<TItem>;
  onConfirm: (selected: readonly TItem[]) => void;
  renderPickToolbar?: ComponentType;
  title?: string;
  width?: 'lg' | 'xl';
  getKey: (item: TItem) => string | number;
  pickModalSearchPlaceholder?: string;
}

export function AsyncSelectorPickModal<TItem>({
  open,
  onClose,
  initialSelected,
  mode,
  columns,
  suggestions,
  onConfirm,
  renderPickToolbar,
  title,
  width = 'xl',
  getKey,
  pickModalSearchPlaceholder,
}: AsyncSelectorPickModalProps<TItem>) {
  const { t } = useTranslation('asyncSelector');
  const [draftSelected, setDraftSelected] = useState<readonly TItem[]>([]);

  useEffect(() => {
    if (open) {
      setDraftSelected(initialSelected);
    }
  }, [open, initialSelected]);

  const handleSelect = (item: TItem) => {
    if (mode === 'single') {
      setDraftSelected([item]);
    } else {
      const key = getKey(item);
      const exists = draftSelected.some((existing) => getKey(existing) === key);
      if (exists) {
        setDraftSelected(draftSelected.filter((existing) => getKey(existing) !== key));
      } else {
        setDraftSelected([...draftSelected, item]);
      }
    }
  };

  const handleConfirm = () => {
    onConfirm(draftSelected);
    onClose();
  };

  const modalTitle = title ?? t('pickModal.title');
  const confirmLabel =
    mode === 'multi'
      ? t('pickModal.useSelectionN', { count: draftSelected.length })
      : t('pickModal.useSelection');

  const totalPages = Math.max(1, Math.ceil(suggestions.totalCount / suggestions.pageSize));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={modalTitle}
      width={width}
      align="top"
      headerVariant="primary"
      closeOnBackdrop={false}
      subheader={
        <SelectorPickerToolbar
          query={suggestions.query}
          onQueryChange={suggestions.setQuery}
          totalCount={suggestions.totalCount}
          pickedCount={draftSelected.length}
          searchPlaceholder={pickModalSearchPlaceholder}
          renderPickToolbar={renderPickToolbar}
        />
      }
      footer={
        <SelectorPickModalFooter
          currentPage={suggestions.page}
          totalPages={totalPages}
          pageSize={suggestions.pageSize}
          onPageChange={suggestions.setPage}
          onPageSizeChange={suggestions.setPageSize}
          actions={
            <>
              <Button variant="outline" onClick={onClose}>
                {t('pickModal.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                disabled={draftSelected.length === 0}
              >
                {confirmLabel}
              </Button>
            </>
          }
        />
      }
      bodyClassName="flex flex-col"
    >
      <SelectorPickerContent
        mode={mode}
        columns={columns}
        selected={draftSelected}
        onSelect={handleSelect}
        onToggleSelection={handleSelect}
        getKey={getKey}
        suggestions={suggestions}
      />
    </Dialog>
  );
}
